import axios from "axios";

const JAGRAN_NEXT_API_BASE_URL =
  process.env.REACT_APP_JAGRAN_NEXT_API_BASE_URL || "/jagran-next-api";

const client = axios.create({
  baseURL: JAGRAN_NEXT_API_BASE_URL,
  timeout: 20000,
});

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

export const unwrapList = (body) => {
  if (Array.isArray(body)) return { rows: body, total: body.length };
  const rows = Array.isArray(body?.data) ? body.data : [];
  const totalValue = Number(body?.total ?? body?.meta?.total ?? body?.pagination?.total);
  return { rows, total: Number.isFinite(totalValue) ? totalValue : null };
};

export const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.message) return data.message;
  if (typeof data?.error === "string") {
    // The API sends the reason per field in `details`, e.g. "name must be lowercase letters...".
    const reasons = Array.isArray(data.details)
      ? data.details.map((item) => item?.message).filter(Boolean).join("; ")
      : "";
    return reasons ? `${data.error}: ${reasons}` : data.error;
  }
  if (Array.isArray(data?.errors)) {
    return data.errors
      .map((item) => item?.message || (typeof item === "string" ? item : ""))
      .filter(Boolean)
      .join(", ");
  }
  return error?.message || "Request failed";
};

export const jagranNextService = {
  listFieldTypes: () => client.get("/api/v1/registration-field-types"),

  listCampaigns: (params) =>
    client.get("/api/v1/campaigns", { params: cleanParams({ limit: 20, offset: 0, ...params }) }),
  getCampaign: (campaignId) => client.get(`/api/v1/campaigns/${campaignId}`),
  createCampaign: (body) => client.post("/api/v1/campaigns", body),
  updateCampaign: (campaignId, body) => client.patch(`/api/v1/campaigns/${campaignId}`, body),
  closeCampaign: (campaignId) => client.post(`/api/v1/campaigns/${campaignId}/close`),
  archiveCampaign: (campaignId) => client.post(`/api/v1/campaigns/${campaignId}/archive`),
  deleteCampaign: (campaignId) => client.delete(`/api/v1/campaigns/${campaignId}`),

  listSeasons: (campaignId, params) =>
    client.get(`/api/v1/campaigns/${campaignId}/seasons`, {
      params: cleanParams({ limit: 20, offset: 0, ...params }),
    }),
  getSeason: (campaignId, seasonId) =>
    client.get(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}`),
  createSeason: (campaignId, body) => client.post(`/api/v1/campaigns/${campaignId}/seasons`, body),
  updateSeason: (campaignId, seasonId, body) =>
    client.patch(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}`, body),
  deleteSeason: (campaignId, seasonId) =>
    client.delete(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}`),

  listPromos: (campaignId, seasonId, params) =>
    client.get(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}/promo-codes`, {
      params: cleanParams({ limit: 20, offset: 0, ...params }),
    }),
  createPromo: (campaignId, seasonId, body) =>
    client.post(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}/promo-codes`, body),
  updatePromo: (campaignId, seasonId, promoId, body) =>
    client.patch(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}/promo-codes/${promoId}`, body),
  deletePromo: (campaignId, seasonId, promoId) =>
    client.delete(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}/promo-codes/${promoId}`),

  listFields: (campaignId, seasonId, params) =>
    client.get(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}/registration-fields`, {
      params: cleanParams({ limit: 50, offset: 0, ...params }),
    }),
  createField: (campaignId, seasonId, body) =>
    client.post(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}/registration-fields`, body),
  updateField: (campaignId, seasonId, fieldId, body) =>
    client.patch(
      `/api/v1/campaigns/${campaignId}/seasons/${seasonId}/registration-fields/${fieldId}`,
      body
    ),
  deleteField: (campaignId, seasonId, fieldId) =>
    client.delete(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}/registration-fields/${fieldId}`),

  listSeasonRegistrations: (campaignId, seasonId, params) =>
    client.get(`/api/v1/campaigns/${campaignId}/seasons/${seasonId}/registrations`, {
      params: cleanParams({ limit: 20, offset: 0, ...params }),
    }),
  deleteRegistration: (campaignId, seasonId, registrationId) =>
    client.delete(
      `/api/v1/campaigns/${campaignId}/seasons/${seasonId}/registrations/${registrationId}`
    ),
  listProductRegistrations: (productId, params) =>
    client.get(`/api/v1/products/${productId}/registrations`, {
      params: cleanParams({ limit: 20, offset: 0, ...params }),
    }),
};

export default jagranNextService;
